// frontend/src/components/pages/chat/FileAttachment.jsx
import React, {
  useRef,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";

const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB
const MAX_FILES = 10; // Maximum number of files
const ALLOWED_TYPES = [
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp",
  "application/pdf",
  "application/msword", // DOC
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // DOCX
  "application/vnd.ms-excel", // XLS
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // XLSX
];

const FileAttachment = forwardRef(
  ({ onFileSelect, onRemove, file, files, hideButton }, ref) => {
    const fileInputRef = useRef();
    const [errorMsg, setErrorMsg] = useState("");
    
    // Store files with their preview URLs together
    // This avoids the useEffect timing issues
    const [fileData, setFileData] = useState([]);
    
    // Cleanup function for URLs
    const cleanupUrls = (data) => {
      if (!data || !Array.isArray(data)) return;
      data.forEach(item => {
        if (item?.previewUrl) {
          try {
            URL.revokeObjectURL(item.previewUrl);
          } catch (e) {
            // Ignore cleanup errors
          }
        }
      });
    };

    // Expose openFileDialog to parent
    useImperativeHandle(ref, () => ({
      openFileDialog: () => {
        if (fileInputRef.current) fileInputRef.current.click();
      },
    }));

    // Sync fileData with props (file/files)
    useEffect(() => {
      // Get current files from props
      let currentFiles = [];
      if (files && Array.isArray(files)) {
        currentFiles = files.filter(f => f && (f instanceof File || f instanceof Blob));
      } else if (file && (file instanceof File || file instanceof Blob)) {
        currentFiles = [file];
      }
      
      // If no files, clear everything
      if (currentFiles.length === 0) {
        cleanupUrls(fileData);
        setFileData([]);
        return;
      }
      
      // Check if we need to update (compare by file properties)
      const currentKeys = currentFiles.map(f => `${f.name}_${f.size}_${f.lastModified}`).join('|');
      const existingKeys = fileData.map(d => `${d.file?.name}_${d.file?.size}_${d.file?.lastModified}`).join('|');
      
      if (currentKeys === existingKeys) {
        // Same files, no update needed
        return;
      }
      
      // Files have changed - create new data array
      cleanupUrls(fileData);
      
      const newFileData = currentFiles.map(f => {
        const type = f.type || 'application/octet-stream';
        let previewUrl = null;
        
        // Only create preview for images
        if (type.startsWith('image/') && f instanceof File) {
          try {
            // Validate file before creating URL
            if (!f || !(f instanceof File || f instanceof Blob)) {
              console.error('[FileAttachment] Invalid file object', {
                file: f,
                type: typeof f,
                isFile: f instanceof File,
                isBlob: f instanceof Blob,
                constructor: f?.constructor?.name
              });
            } else {
              previewUrl = URL.createObjectURL(f);
              console.log('[FileAttachment] Successfully created preview URL', {
                fileName: f.name,
                fileType: f.type,
                fileSize: f.size
              });
            }
          } catch (e) {
            console.error('[FileAttachment] Failed to create preview URL', {
              error: e.message,
              errorName: e.name,
              fileName: f?.name,
              fileType: f?.type,
              fileSize: f?.size,
              isFile: f instanceof File,
              isBlob: f instanceof Blob
            });
          }
        }
        
        return {
          file: f,
          type: type,
          previewUrl: previewUrl
        };
      });
      
      setFileData(newFileData);
      
      // Cleanup on unmount
      return () => {
        cleanupUrls(newFileData);
      };
    }, [file, files]); // Only depend on props

    const handleFileChange = (e) => {
      const selectedFiles = Array.from(e.target.files || []);
      
      if (selectedFiles.length === 0) {
        e.target.value = "";
        return;
      }
      
      // Get current valid files
      const existingFiles = fileData.map(d => d.file).filter(f => f instanceof File);
      
      // Check total number of files (including existing ones)
      const totalFiles = existingFiles.length + selectedFiles.length;
      if (totalFiles > MAX_FILES) {
        setErrorMsg(`Maximum ${MAX_FILES} files allowed. You selected ${selectedFiles.length} file(s), but you already have ${existingFiles.length} file(s).`);
        e.target.value = "";
        return;
      }
      
      // Validate each file
      const validFiles = [];
      const errors = [];
      
      for (let i = 0; i < selectedFiles.length; i++) {
        const file = selectedFiles[i];
        
        // Validate file size
        if (file.size > MAX_FILE_SIZE) {
          errors.push(`${file.name}: File size too large (max 50MB)`);
          continue;
        }
        
        // Validate file type (check both MIME type and extension)
        const fileExtension = file.name.split('.').pop()?.toLowerCase();
        const isValidType = ALLOWED_TYPES.includes(file.type) ||
          ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'jpg', 'jpeg', 'png', 'gif', 'webp'].includes(fileExtension);
        
        if (!isValidType) {
          errors.push(`${file.name}: Invalid file type. Allowed: JPG, PNG, GIF, WEBP, PDF, DOC, DOCX, XLS, XLSX`);
          continue;
        }
        
        validFiles.push(file);
      }
      
      if (errors.length > 0) {
        setErrorMsg(errors.join('; '));
        e.target.value = "";
        if (validFiles.length === 0) return; // Don't proceed if all files are invalid
      } else {
        setErrorMsg("");
      }
      
      // Call onFileSelect with all valid files (for multiple file support)
      if (validFiles.length > 0) {
        const allFiles = [...existingFiles, ...validFiles];
        
        // If we have multiple files, always pass array
        // If we have exactly one file and no existing files, pass single file for backward compatibility
        if (allFiles.length > 1 || validFiles.length > 1) {
          onFileSelect(allFiles);
        } else {
          onFileSelect(validFiles[0]);
        }
      }
      
      // Always reset the input value so selecting the same file again works
      e.target.value = "";
    };

    // Helper to get file extension in uppercase
    const getFileExtension = (name, type) => {
      if (type && type.startsWith("image/")) return null;
      if (!name) return "FILE";
      const ext = name.split(".").pop();
      if (ext && ext.length <= 5) return ext.toUpperCase();
      // fallback for long/unknown extensions
      if (type && type.includes("/"))
        return type.split("/").pop().toUpperCase();
      return "FILE";
    };
    
    const handleRemoveFile = (index) => {
      const currentFiles = fileData.map(d => d.file).filter(f => f instanceof File);
      
      // If we have multiple files, remove specific file
      if (currentFiles.length > 1) {
        const newFiles = currentFiles.filter((_, i) => i !== index);
        onFileSelect(newFiles.length > 0 ? newFiles : null);
      } else {
        // Single file mode: remove file
        onRemove();
      }
    };

    return (
      <div className={`file-attachment-container flex items-center gap-3 ${fileData.length > 0 ? "mb-0" : ""}`}>
        {/* Hidden file input */}
        <input
          type="file"
          ref={fileInputRef}
          className="hidden"
          onChange={handleFileChange}
          multiple
          accept="image/jpeg,image/png,image/gif,image/webp,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        />
        {/* Attach button if no files and not hidden by parent */}
        {fileData.length === 0 && !hideButton && (
          <button
            type="button"
            className="file-attach-btn flex items-center gap-1 px-3 py-1 rounded-lg bg-bg-40 hover:bg-bg-50 text-fg-70 text-sm border border-bd-50"
            onClick={() => fileInputRef.current.click()}
          >
            <span className="icon-paperclip" role="img" aria-label="Attach">
              📎
            </span>
            <span>Attach file{files ? 's' : ''}</span>
          </button>
        )}
        {/* Error message */}
        {errorMsg && (
          <div className="text-red-500 text-xs mt-1 ml-1 flex items-center gap-1">
            <span>{errorMsg}</span>
            <button
              type="button"
              className="text-red-400 hover:text-red-600 focus:outline-none p-0.5"
              style={{ lineHeight: 1 }}
              onClick={() => setErrorMsg("")}
              aria-label="Dismiss error"
            >
              <span
                style={{
                  fontSize: "1rem",
                  fontWeight: "bold",
                  display: "inline-block",
                }}
              >
                &times;
              </span>
            </button>
          </div>
        )}
        {/* Preview if files selected */}
        {fileData.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-0">
            {fileData.map((data, index) => {
              if (!data || !data.file) return null;
              const { file: f, type: fileType, previewUrl } = data;
              
              return (
                <div key={`${f.name}_${f.size}_${index}`} className="file-preview flex items-center gap-2 bg-bg-40 rounded-lg p-2 border border-bd-50">
                  {fileType.startsWith("image/") && previewUrl && (
                    <img
                      src={previewUrl}
                      alt="preview"
                      className="file-preview-img w-12 h-12 object-cover rounded"
                    />
                  )}
                  {/* Modern file placeholder for non-image files */}
                  {!fileType.startsWith("image/") && (
                    <span
                      className="file-ext-badge flex items-center justify-center w-12 h-12 rounded-lg font-bold text-xs text-white mr-2 select-none border-none"
                      style={{
                        background:
                          "linear-gradient(135deg, #60a5fa 0%, #2563eb 100%)",
                        backgroundColor: "var(--tw-gradient-stops)",
                        ...(window.matchMedia &&
                        window.matchMedia("(prefers-color-scheme: dark)").matches
                          ? {
                              background:
                                "linear-gradient(135deg, #1e40af 0%, #1e293b 100%)",
                            }
                          : {}),
                      }}
                    >
                      {getFileExtension(f.name, fileType)}
                    </span>
                  )}
                  <div className="file-info flex flex-col">
                    <span className="file-name text-fg-80 text-sm font-mono max-w-[150px] truncate">
                      {f.name}
                    </span>
                    <span className="file-size text-fg-60 text-xs font-mono">
                      {(f.size / 1024).toFixed(1)} KB
                    </span>
                  </div>
                  <button
                    type="button"
                    className="file-remove-btn ml-2 text-red-500 hover:text-red-700 text-lg"
                    onClick={() => handleRemoveFile(index)}
                    title="Remove file"
                  >
                    ×
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }
);

export default FileAttachment;
